const RECOVERABLE_STATUSES = new Set([401, 419])
const HEARTBEAT_INTERVAL_MS = 30_000
const ACTIVE_WINDOW_MS = 60_000

function firstValidationMessage(payload) {
    const errors = payload?.errors

    if (errors && typeof errors === 'object') {
        const firstError = Object.values(errors).flat().find(Boolean)

        if (firstError) {
            return firstError
        }
    }

    return payload?.message || 'We could not verify those details. Please try again.'
}

function isSameOrigin(request, location) {
    return new URL(request.url, location.href).origin === location.origin
}

function hasRequestBody(method) {
    return !['GET', 'HEAD'].includes(method.toUpperCase())
}

export async function withCsrfToken(request, token) {
    const headers = new Headers(request.headers)
    headers.set('X-CSRF-TOKEN', token)
    headers.set('X-Session-Recovery-Attempt', '1')

    if (!hasRequestBody(request.method)) {
        return new Request(request, { headers })
    }

    const contentType = headers.get('Content-Type') || ''
    let body

    if (contentType.includes('application/json')) {
        const rawBody = await request.clone().text()

        try {
            const jsonBody = JSON.parse(rawBody)

            if (jsonBody && typeof jsonBody === 'object' && '_token' in jsonBody) {
                jsonBody._token = token
            }

            body = JSON.stringify(jsonBody)
        } catch {
            body = rawBody
        }
    } else if (contentType.includes('application/x-www-form-urlencoded')) {
        const formBody = new URLSearchParams(await request.clone().text())
        formBody.set('_token', token)
        body = formBody
    } else if (contentType.includes('multipart/form-data')) {
        const formBody = await request.clone().formData()
        formBody.set('_token', token)
        headers.delete('Content-Type')
        body = formBody
    } else {
        body = await request.clone().blob()
    }

    return new Request(request, { body, headers })
}

function applyCsrfToken(document, axios, token) {
    const meta = document.querySelector('meta[name="csrf-token"]')

    if (meta) {
        meta.content = token
    }

    for (const input of document.querySelectorAll('input[name="_token"]')) {
        input.value = token
    }

    axios.defaults.headers.common['X-CSRF-TOKEN'] = token
}

export function withAxiosCsrfToken(config, token) {
    config.headers = config.headers || {}
    config.headers['X-CSRF-TOKEN'] = token

    if (config.data instanceof FormData || config.data instanceof URLSearchParams) {
        if (config.data.has('_token')) {
            config.data.set('_token', token)
        }

        return config
    }

    if (config.data && 'object' === typeof config.data && Object.hasOwn(config.data, '_token')) {
        config.data = { ...config.data, _token: token }

        return config
    }

    if ('string' === typeof config.data) {
        try {
            const jsonBody = JSON.parse(config.data)

            if (jsonBody && 'object' === typeof jsonBody && Object.hasOwn(jsonBody, '_token')) {
                jsonBody._token = token
                config.data = JSON.stringify(jsonBody)
            }
        } catch {
            const formBody = new URLSearchParams(config.data)

            if (formBody.has('_token')) {
                formBody.set('_token', token)
                config.data = formBody.toString()
            }
        }
    }

    return config
}

export function createModalReauthentication({ document, nativeFetch, refreshState }) {
    let activePromise = null

    return function reauthenticate() {
        if (activePromise) {
            return activePromise
        }

        const modal = document.querySelector('#session-reauthentication')
        const form = modal?.querySelector('[data-session-reauthentication-form]')

        if (!modal || !form) {
            return Promise.reject(new Error('The session reauthentication form is unavailable.'))
        }

        const credentials = form.querySelector('[data-session-credentials]')
        const twoFactor = form.querySelector('[data-session-two-factor]')
        const error = form.querySelector('[data-session-reauthentication-error]')
        const submit = form.querySelector('[data-session-reauthentication-submit]')
        let requiresTwoFactor = false

        modal.classList.remove('hidden')
        modal.classList.add('flex')
        document.body.style.overflow = 'hidden'
        form.querySelector('[name="email"]')?.focus()

        activePromise = new Promise((resolve) => {
            const showError = (message) => {
                error.textContent = message
                error.classList.remove('hidden')
            }

            const submitCredentials = async (event) => {
                event.preventDefault()
                error.classList.add('hidden')
                submit.disabled = true

                try {
                    const state = await refreshState()
                    const url = requiresTwoFactor ? modal.dataset.twoFactorUrl : modal.dataset.loginUrl
                    const payload = requiresTwoFactor
                        ? {
                            code: form.elements.code.value || null,
                            recovery_code: form.elements.recovery_code.value || null,
                        }
                        : {
                            email: form.elements.email.value,
                            password: form.elements.password.value,
                            remember: false,
                        }
                    const response = await nativeFetch(url, {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': state.token,
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                        body: JSON.stringify(payload),
                    })
                    const responsePayload = await response.json().catch(() => ({}))

                    if (!response.ok) {
                        showError(firstValidationMessage(responsePayload))
                        return
                    }

                    if (!requiresTwoFactor && responsePayload.two_factor) {
                        requiresTwoFactor = true
                        credentials.classList.add('hidden')
                        twoFactor.classList.remove('hidden')
                        form.querySelector('[name="code"]')?.focus()
                        return
                    }

                    const authenticatedState = await refreshState()

                    if (!authenticatedState.authenticated) {
                        showError('Your sign-in could not be restored. Please try again or use the full sign-in page.')
                        return
                    }

                    form.removeEventListener('submit', submitCredentials)
                    form.reset()
                    credentials.classList.remove('hidden')
                    twoFactor.classList.add('hidden')
                    modal.classList.add('hidden')
                    modal.classList.remove('flex')
                    document.body.style.overflow = ''
                    resolve(authenticatedState)
                } catch {
                    showError('We could not reconnect to CPM. Check your connection and try again.')
                } finally {
                    submit.disabled = false
                }
            }

            form.addEventListener('submit', submitCredentials)
        }).finally(() => {
            activePromise = null
        })

        return activePromise
    }
}

export function initializeExpiredSessionPage({ window, document, nativeFetch = window.fetch.bind(window) }) {
    const page = document.querySelector('#session-reauthentication')

    if (!page) {
        return null
    }

    const refreshState = async () => {
        const response = await nativeFetch(page.dataset.csrfUrl, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        })

        if (!response.ok) {
            throw new Error('Unable to refresh the browser session.')
        }

        return response.json()
    }

    const reauthenticate = createModalReauthentication({ document, nativeFetch, refreshState })
    const reauthentication = reauthenticate()

    reauthentication.then(() => {
        if (window.history.length > 1) {
            window.history.back()
            return
        }

        window.location.assign(page.dataset.fallbackUrl)
    })

    return reauthentication
}

export function createSessionRecovery({ axios, window, document, nativeFetch = window.fetch.bind(window), reauthenticate = null }) {
    let currentState = null
    let reauthenticationPromise = null

    const modal = () => document.querySelector('#session-reauthentication')
    const csrfUrl = () => modal()?.dataset.csrfUrl || new URL('session/csrf-token', document.querySelector('meta[name="base-url"]')?.content || window.location.href).href

    const refreshState = async () => {
        const response = await nativeFetch(csrfUrl(), {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        })

        if (!response.ok) {
            throw new Error('Unable to refresh the browser session.')
        }

        currentState = await response.json()
        applyCsrfToken(document, axios, currentState.token)

        return currentState
    }

    const promptForReauthentication = reauthenticate || createModalReauthentication({ document, nativeFetch, refreshState })

    const ensureAuthenticated = async () => {
        if (!reauthenticationPromise) {
            reauthenticationPromise = promptForReauthentication().finally(() => {
                reauthenticationPromise = null
            })
        }

        return reauthenticationPromise
    }

    const recover = async (status, retry) => {
        const state = await refreshState()

        if (401 === status && state.authenticated) {
            return null
        }

        const authenticatedState = state.authenticated ? state : await ensureAuthenticated()

        return retry(authenticatedState.token)
    }

    const checkRestoredPage = async () => {
        try {
            const state = await refreshState()

            if (!state.authenticated) {
                await ensureAuthenticated()
            }
        } catch {
            // A normal request will surface the recovery UI if the restored page is used.
        }
    }

    return { checkRestoredPage, recover, refreshState }
}

export function installAxiosInterceptor(axios, recovery, window) {
    axios.interceptors.response.use(
        response => response,
        async error => {
            const status = error.response?.status
            const config = error.config

            if (!RECOVERABLE_STATUSES.has(status) || !config || config._sessionRecoveryAttempted) {
                return Promise.reject(error)
            }

            const requestUrl = new URL(config.url, window.location.href)

            if (requestUrl.origin !== window.location.origin) {
                return Promise.reject(error)
            }

            try {
                const recoveredResponse = await recovery.recover(status, token => {
                    config._sessionRecoveryAttempted = true

                    return axios.request(withAxiosCsrfToken(config, token))
                })

                return recoveredResponse ?? Promise.reject(error)
            } catch {
                return Promise.reject(error)
            }
        },
    )
}

export function installFetchInterceptor(window, recovery, nativeFetch) {
    window.fetch = async (input, init) => {
        const requestUrl = input instanceof Request ? input.url : new URL(input, window.location.href).href
        const request = input instanceof Request ? new Request(input, init) : new Request(requestUrl, init)

        if (!isSameOrigin(request, window.location) || request.headers.has('X-Session-Recovery-Attempt')) {
            return nativeFetch(input, init)
        }

        const initialRequest = request.clone()
        const retryRequest = request.clone()
        const response = await nativeFetch(initialRequest)

        if (!RECOVERABLE_STATUSES.has(response.status)) {
            return response
        }

        try {
            const recoveredResponse = await recovery.recover(response.status, async token => {
                return nativeFetch(await withCsrfToken(retryRequest, token))
            })

            return recoveredResponse ?? response
        } catch {
            return response
        }
    }
}

export function startActivityAwareHeartbeat({ window, document, now = () => Date.now() }) {
    let lastActivityAt = 0
    let lastHeartbeatAt = 0

    const heartbeat = () => {
        const currentTime = now()

        if (
            'visible' !== document.visibilityState
            || !lastActivityAt
            || currentTime - lastActivityAt > ACTIVE_WINDOW_MS
            || currentTime - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS
            || !document.querySelector('[data-session-heartbeat]')
        ) {
            return false
        }

        lastHeartbeatAt = currentTime
        window.dispatchEvent(new CustomEvent('cpm-session-heartbeat'))

        return true
    }

    const recordActivity = event => {
        if (!event.isTrusted || modalIsOpen(document)) {
            return
        }

        lastActivityAt = now()
        heartbeat()
    }

    for (const eventName of ['keydown', 'pointerdown', 'touchstart', 'scroll']) {
        document.addEventListener(eventName, recordActivity, { passive: true, capture: true })
    }

    const interval = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS)

    return () => {
        window.clearInterval(interval)

        for (const eventName of ['keydown', 'pointerdown', 'touchstart', 'scroll']) {
            document.removeEventListener(eventName, recordActivity, { capture: true })
        }
    }
}

function modalIsOpen(document) {
    const modal = document.querySelector('#session-reauthentication')

    return modal && !modal.classList.contains('hidden')
}

export function initializeSessionRecovery({ axios, window, document }) {
    const nativeFetch = window.fetch.bind(window)
    const recovery = createSessionRecovery({ axios, window, document, nativeFetch })

    installAxiosInterceptor(axios, recovery, window)
    installFetchInterceptor(window, recovery, nativeFetch)

    const start = () => startActivityAwareHeartbeat({ window, document })

    if ('loading' === document.readyState) {
        document.addEventListener('DOMContentLoaded', start, { once: true })
    } else {
        start()
    }

    window.addEventListener('pageshow', event => {
        if (event.persisted) {
            recovery.checkRestoredPage()
        }
    })

    return recovery
}
